/**
 * Authentication state provider.
 *
 * Manages the app's auth lifecycle: loading → onboarding | locked | unlocked.
 *
 * On mount, checks SecureStorage for an `encrypted_mnemonic` key:
 * - Absent → `onboarding` (new user, needs wallet creation)
 * - Present → `locked` (returning user, needs PIN to unlock)
 *
 * State transitions:
 * - `loading` → `onboarding` (no encrypted mnemonic found)
 * - `loading` → `locked` (encrypted mnemonic found)
 * - `locked` → `unlocked` (PIN verified, mnemonic decrypted)
 * - `unlocked` → `locked` (session lock, tab switch, manual lock)
 * - `onboarding` → `unlocked` (wallet created, mnemonic set)
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module AuthProvider
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPlatformServices } from '../lib/platform/index';
import type { SecureStorage } from '../lib/platform/index';

/** SecureStorage key for the encrypted master mnemonic. */
const ENCRYPTED_MNEMONIC_KEY = 'encrypted_mnemonic';

/** Possible authentication states. */
export type AuthState = 'loading' | 'onboarding' | 'locked' | 'unlocked';

/**
 * Value provided by AuthContext.
 */
export interface AuthContextValue {
  /** Current authentication state. */
  readonly state: AuthState;
  /** Whether the user just restored from mnemonic (Evolu data may still be syncing). */
  readonly justRestored: boolean;
  /** Transition to unlocked state. */
  unlock(): void;
  /** Transition to locked state. */
  lock(): void;
  /** Transition to unlocked state from onboarding (after wallet creation). */
  completeOnboarding(): void;
  /** Transition to unlocked state from restore (triggers sync loading state). */
  completeRestore(): void;
  /** Clear the justRestored flag (called when sync data appears or times out). */
  clearJustRestored(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Props for AuthProvider. */
interface AuthProviderProps {
  readonly children: ReactNode;
}

/**
 * Provider that manages authentication state.
 *
 * Must wrap any component that needs to know the auth state.
 * Checks SecureStorage on mount to determine initial state.
 *
 * Tier: All tiers (core infrastructure).
 */
/** Session storage key for the just-restored flag (survives page reload). */
const JUST_RESTORED_KEY = 'satsflow_just_restored';

export function AuthProvider({ children }: AuthProviderProps): ReactNode {
  const [state, setState] = useState<AuthState>('loading');
  const [justRestored, setJustRestored] = useState(() => {
    // Check sessionStorage on mount — the flag survives the post-restore reload
    const stored = sessionStorage.getItem(JUST_RESTORED_KEY);
    if (stored === 'true') {
      sessionStorage.removeItem(JUST_RESTORED_KEY);
      return true;
    }
    return false;
  });

  useEffect(() => {
    let cancelled = false;

    async function checkStoredMnemonic(): Promise<void> {
      try {
        const services = await createPlatformServices();
        const storage: SecureStorage = services.secureStorage;
        const encrypted = await storage.retrieve(ENCRYPTED_MNEMONIC_KEY);

        if (cancelled) return;

        if (encrypted !== null) {
          // Always show lock screen — even after restore. The user must enter
          // their PIN to decrypt the mnemonic into memory. Without this, the
          // app is "unlocked" but has no usable keys (Nostr, npub.cash, etc.).
          setState('locked');
        } else {
          setState('onboarding');
        }
      } catch {
        // If storage check fails, assume onboarding (safe default)
        if (!cancelled) {
          setState('onboarding');
        }
      }
    }

    void checkStoredMnemonic();

    return () => {
      cancelled = true;
    };
  }, []);

  const unlock = useCallback((): void => {
    setState('unlocked');
  }, []);

  const lock = useCallback((): void => {
    setState('locked');
  }, []);

  const completeOnboarding = useCallback((): void => {
    setState('unlocked');
  }, []);

  const completeRestore = useCallback((): void => {
    // Persist flag to sessionStorage so it survives the reload
    sessionStorage.setItem(JUST_RESTORED_KEY, 'true');
    // Reload the page so Evolu re-initializes all query subscriptions
    // with the synced CRDT data. Without this, queries set up before
    // restoreAppOwner (with reload:false) don't pick up the newly
    // synced rows (connections, transactions, etc.).
    window.location.href = '/';
  }, []);

  const clearJustRestored = useCallback((): void => {
    setJustRestored(false);
  }, []);

  const value: AuthContextValue = {
    state,
    justRestored,
    unlock,
    lock,
    completeOnboarding,
    completeRestore,
    clearJustRestored,
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}

/**
 * Access the auth context.
 *
 * Must be called within an AuthProvider. Throws if used outside.
 *
 * @returns AuthContextValue with auth state and controls
 * @throws Error if called outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
